/**
 * Rate Connect pause flow reason configuration
 * Defines grouped reasons for pausing Rate Connect campaigns
 * Used in pause flow survey
 */

/** Pause reason group header */
export interface PauseReasonGroup {
  value: string
  label: string
}

/** Individual pause reason option */
export interface PauseReasonOption {
  id: number
  group: string
  value: string
  label: string
}

/** Grouped pause reasons structure */
export interface PauseReasonCategory {
  /** Group header information */
  group: PauseReasonGroup
  /** Available options in this group */
  options: readonly PauseReasonOption[]
}

export const reasons: readonly PauseReasonCategory[] = [
  {
    group: { value: 'Business operations', label: 'rc_pause_flow_menu_header_1' },
    options: [
      {
        id: 0,
        group: 'Business operations',
        value: 'I do not need to advertise during the low or off-season',
        label: 'rc_pause_survey_reason_temporary',
      },
      {
        id: 1,
        group: 'Business operations',
        value: 'I am focusing on other marketing channels',
        label: 'rc_pause_survey_reason_marketing',
      },
      {
        id: 2,
        group: 'Business operations',
        value: 'I am currently booked up',
        label: 'rc_pause_survey_reason_occupancy',
      },
      {
        id: 3,
        group: 'Business operations',
        value: 'I am changing connectivity providers',
        label: 'rc_pause_survey_reason_connectivity',
      },
    ],
  },
  {
    group: { value: 'Conversion and traffic', label: 'rc_pause_flow_menu_header_2' },
    options: [
      {
        id: 4,
        group: 'Conversion and traffic',
        value: 'I do not receive enough bookings',
        label: 'rc_pause_survey_reason_performance',
      },
      {
        id: 5,
        group: 'Conversion and traffic',
        value: 'I do not find my website rates on trivago',
        label: 'rc_pause_survey_reason_problem',
      },
      {
        id: 6,
        group: 'Conversion and traffic',
        value: 'I do not receive enough traffic from trivago to my website',
        label: 'rc_pause_survey_reason_traffic',
      },
    ],
  },
  {
    group: { value: 'Clarity and understanding', label: 'rc_pause_flow_menu_header_3' },
    options: [
      {
        id: 7,
        group: 'Clarity and understanding',
        value: 'I do not fully understand how Rate Connect works',
        label: 'rc_pause_survey_reason_understanding',
      },
      {
        id: 8,
        group: 'Clarity and understanding',
        value: 'I am not satisfied with Rate Connect',
        label: 'rc_pause_survey_reason_functionality',
      },
    ],
  },
  {
    group: { value: 'Other reasons', label: 'rc_pause_flow_menu_header_4' },
    options: [
      {
        id: 9,
        group: 'Other reasons',
        value: 'My property has permanently closed ',
        label: 'rc_pause_survey_reason_property',
      },
      {
        id: 10,
        group: 'Other reasons',
        value: 'My property has changed ownership',
        label: 'rc_pause_survey_reason_ownership',
      },
      {
        id: 11,
        group: 'Other reasons',
        value: 'Other',
        label: 'rc_pause_survey_reason_other',
      },
    ],
  },
] as const

/** Valid pause reason IDs */
export type PauseReasonId = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11

/** Pause reason group names */
export type PauseReasonGroupName = 
  | 'Business operations'
  | 'Conversion and traffic'
  | 'Clarity and understanding'
  | 'Other reasons'

/**
 * Get all pause reasons as flat array
 * @returns Array of all pause reason options
 */
export function getAllPauseReasons(): readonly PauseReasonOption[] {
  return reasons.flatMap(category => category.options)
}

/**
 * Get pause reason by ID
 * @param id - Reason ID
 * @returns Pause reason option or undefined
 */
export function getPauseReasonById(id: number): PauseReasonOption | undefined {
  return getAllPauseReasons().find(reason => reason.id === id)
}